import { useQueryClient } from '@tanstack/react-query';

import {
  createPurchaseRequest,
  updatePurchaseRequest,
} from '@/Shared/RequestDialog/components/EditDialog/api/HandRequest.api';
import type {
  SaveRequestDto,
  UpdateRequestDto,
} from '@/Shared/RequestDialog/components/EditDialog/api/HandRequest.interface';
import { refetchAllQueries } from '@/Shared/RequestDialog/components/EditDialog/utils/refetchAllQueries';

import { useSaveMutation } from './useSaveMutation';

/**
 * Custom hook for safely saving purchase requests with duplicate prevention
 *
 * This hook wraps the purchase request API with protection against duplicate submissions.
 * It ensures that a request will never be sent twice, even if the user clicks save multiple times.
 */
export function useSavePurchaseRequest() {
  const queryClient = useQueryClient();

  // Create request mutation with duplicate prevention
  const createMutation = useSaveMutation(createPurchaseRequest, {
    mutationKey: ['createPurchaseRequest'],
    successMessage: 'تم إنشاء الطلب بنجاح',
    errorMessage: 'حدث خطأ أثناء إنشاء الطلب',
    onSuccess: () => {
      // Invalidate queries to refresh data
      refetchAllQueries(queryClient);
    },
  });

  // Update request mutation with duplicate prevention
  const updateMutation = useSaveMutation(updatePurchaseRequest, {
    mutationKey: ['updatePurchaseRequest'],
    successMessage: 'تم تعديل الطلب بنجاح',
    errorMessage: 'حدث خطأ أثناء تعديل الطلب',
    onSuccess: () => {
      // Invalidate queries to refresh data
      refetchAllQueries(queryClient);
    },
  });

  return {
    // Safe function to create a purchase request
    createRequest: (data: SaveRequestDto) => createMutation.save(data),
    // Safe function to update a purchase request
    updateRequest: (data: UpdateRequestDto) => updateMutation.save(data),
    // Loading states
    isCreating: createMutation.isSubmitting,
    isUpdating: updateMutation.isSubmitting,
    // Combine loading states for any operation
    isSubmitting: createMutation.isSubmitting || updateMutation.isSubmitting,
  };
}
